// src/admin/pages/AdminOverviewPage.jsx
//
// Superadmin overview ("vedi come tenant"): pick a tenant from the dropdown and
// render that tenant's dashboard via the shared DashboardView. This is the
// per-tenant deep-dive surface — a cross-tenant aggregate would need a BE
// group_by=tenant endpoint (or a per-tenant fan-out) and is a separate step.
//
// Reuses the SA tenant-dropdown pattern from RunListPage (adminListTenants → t.id),
// with per-user localStorage persistence of the last-viewed tenant.
//
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardView } from '../../sections/Dashboard.jsx';
import { adminListTenants } from '../adminApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { COLORS, FONT_SANS } from '../../constants/theme.js';

const FONT = FONT_SANS;

export default function AdminOverviewPage() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState('');

  const storageKey = `admin_overview_tenant:${user?.user_id || user?.email || 'anon'}`;

  useEffect(() => {
    adminListTenants()
      .then((list) => {
        const rows = Array.isArray(list) ? list : [];
        setTenants(rows);
        // Hydrate from last-viewed tenant only if it still exists.
        try {
          const last = localStorage.getItem(storageKey);
          if (last && rows.some(t => t.id === last)) setSelected(last);
        } catch { /* private mode */ }
      })
      .catch(() => setTenants([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onPick(id) {
    setSelected(id);
    try {
      if (id) localStorage.setItem(storageKey, id);
      else localStorage.removeItem(storageKey);
    } catch { /* private mode */ }
  }

  const selectedName = tenants.find(t => t.id === selected)?.display_name || '';

  const header = (
    <>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: 4, fontFamily: FONT }}>{t('overview.title')}</h1>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '0 0 16px', fontFamily: FONT }}>
        {t('overview.subtitle')}{selectedName ? ` — ${selectedName}` : ''}
      </p>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, fontFamily: FONT }}>
        <label style={{ fontSize: 13, color: COLORS.ink }}>{t('shared.tenantLabel')}</label>
        <select
          value={selected}
          onChange={e => onPick(e.target.value)}
          style={{
            padding: '6px 10px', borderRadius: 6,
            border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
            fontSize: 13, fontFamily: FONT, minWidth: 240,
          }}
        >
          <option value="">{t('shared.selectPlaceholder')}</option>
          {tenants.map(tn => (
            <option key={tn.id} value={tn.id}>{tn.display_name || tn.id}</option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <DashboardView
      tenantId={selected || undefined}
      isSuperadmin
      headerSlot={header}
    />
  );
}
